import React, { FC } from 'react';
import { useSession } from 'next-auth/react';
import { StoreItemView } from '@specs/views';
import { collectionMeta, drop, storeItem, toy } from '@specs/specs';
import { Button, ButtonProps } from '@components/UIKit/Button/Button';
import { useTranslation } from '@hooks/useTranslation';
import { useRouter } from 'next/router';
import { LinksEnum } from '@specs/navigation';
import { gtagEventTrigger } from '@specs/helpers';
import { BuyToyButton } from '@components/Pages/ToyPage/Info/Buttons/BuyToyButton';
import { CollectButton } from '@components/Pages/ToyPage/Info/Buttons/CollectButton';
import { CollectBonusToyButton } from '@components/Pages/ToyPage/Info/Buttons/CollectBonusToyButton';
import { useShowToast } from '@hooks/useShowToast';
import { GenerateQrButton } from '@components/Pages/ToyPage/Info/Buttons/GenerateQrButton';
interface Props {
  toyId: toy['id'];
  slug: toy['slug'];
  toyName: toy['name'];
  imageid: toy['imageid'];
  publication: toy['publication'];
  collectionId: collectionMeta['id'] | undefined;
  haveFigurinesForSale: boolean;
  firstStoreItem: StoreItemView | undefined;
  myFirstStoreItem: storeItem | undefined;
  currentDrop: drop | undefined;
  isLoading: boolean;
  isArtClub: boolean;
  isArtClubMember: boolean;
  isCollectable: boolean;
  isBonusToy: boolean;
  isHaveToyInCollection: boolean;
  iAmTheCreator: boolean;
  buttonProps?: Omit<ButtonProps, 'children' | 'id'>;
}
const defaultButtonProps: Omit<ButtonProps, 'children' | 'id'> = {
  color: 'Black',
  size: 'large'
};
const isCorrectStoreItem = (storeItem: StoreItemView | undefined): storeItem is Omit<StoreItemView, 'instanceid'> & {
  instanceid: number;
} => typeof storeItem !== 'undefined' && typeof storeItem.instanceid === 'number';
export const Buttons: FC<Props> = ({
  collectionId,
  toyName,
  imageid,
  publication,
  toyId,
  currentDrop,
  isCollectable,
  isLoading,
  isArtClubMember,
  isArtClub,
  slug,
  haveFigurinesForSale,
  firstStoreItem,
  myFirstStoreItem,
  isBonusToy,
  isHaveToyInCollection,
  iAmTheCreator,
  buttonProps
}) => {
  const router = useRouter();
  const {
    showToast
  } = useShowToast();
  const {
    status
  } = useSession();
  const {
    translate: tr
  } = useTranslation();
  const showArtClubJoinButton = isArtClub && !isArtClubMember;
  const editToyHandler = async () => {
    await router.push({
      pathname: LinksEnum.TOY_SELL,
      query: {
        slug
      }
    });
  };
  const totalButtonProps = {
    ...defaultButtonProps,
    ...buttonProps
  };
  const sellToyHandler = async () => {
    if (!haveFigurinesForSale) {
      showToast({
        variant: 'info',
        title: 'toy.page.sell.without.instance.id.description'
      });
      return;
    }
    await router.push({
      pathname: LinksEnum.TOY_SELL,
      query: {
        slug
      }
    });
  };
  if (status === 'unauthenticated') return <Button id='join_rtoys_button' {...totalButtonProps} onClick={async () => {
    await router.push(LinksEnum.SIGN_IN);
  }} icons={{
    left: {
      name: 'user'
    }
  }}>
            {tr({
      id: 'btn.join'
    })}
        </Button>;
  if (isLoading) return <Button id='loading_button' {...totalButtonProps} loading>
            {tr({
      id: 'btn.loading'
    })}
        </Button>;
  if (iAmTheCreator) return publication === 'TWIN' ? <GenerateQrButton id='generate_qr_button' {...totalButtonProps} toyId={toyId} name={toyName} slug={slug} /> : null;
  if (showArtClubJoinButton) return <Button id='join_art_club_button' {...totalButtonProps} onClick={async () => {
    gtagEventTrigger({
      action: 'go_to_chikoroko_art',
      category: 'commercial'
    });
    await router.push(LinksEnum.JOIN_ART_CLUB);
  }}>
            {tr({
      id: 'btn.join.art.club'
    })}
        </Button>;
  // TODO: Костыль, нужно понять почему платный дроп не пропадает по истечению времени
  if (isCollectable && !!currentDrop && currentDrop.finish.getTime() > new Date().getTime()) return <CollectButton id='collect_drop_button' {...totalButtonProps} toyId={toyId} currentDrop={currentDrop} imageid={imageid} slug={slug} />;
  if (isBonusToy && isCollectable && typeof collectionId === 'number') return <CollectBonusToyButton id='collect_bonus_drop_button' {...totalButtonProps} toyId={toyId} slug={slug} collectionId={collectionId} />;
  if (myFirstStoreItem) return <Button id='edit_toy_button' {...totalButtonProps} onClick={editToyHandler} icons={{
    left: {
      name: 'edit'
    }
  }} color={'White'}>
            {tr({
      id: 'btn.edit'
    })}
        </Button>;
  if (isHaveToyInCollection) return <Button id='sell_toy_button' {...totalButtonProps} onClick={sellToyHandler} color={'White'}>
            {tr({
      id: 'btn.sell'
    })}
        </Button>;
  if (isCorrectStoreItem(firstStoreItem)) return <BuyToyButton id='buy_payable_drop_button' firstStoreItem={firstStoreItem} toyName={toyName} {...totalButtonProps} />;
  return null;
};